import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Star, CheckCircle, XCircle, Filter, TrendingUp, Trash2 } from 'lucide-react';
import Button from '../components/ui/Button';
import ConfirmModal from '../components/modals/ConfirmModal';

interface Survey {
  id: string;
  token: string;
  entity_type: string;
  entity_name: string;
  salesperson: string;
  client_name: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
  response?: SurveyResponse;
}

interface SurveyResponse {
  q1_experience_rating: number | null;
  q2_quality_rating: number | null;
  q3_delivery_rating: number | null;
  q4_communication_rating: number | null;
  q5_would_return: boolean | null;
  q6_would_recommend: boolean | null;
  q6_recommendation_target: string | null;
  q7_comment: string | null;
}

function computeSatisfactionIndex(r: SurveyResponse): number | null {
  const ratings = [
    r.q1_experience_rating,
    r.q2_quality_rating,
    r.q3_delivery_rating,
    r.q4_communication_rating,
  ].filter((v): v is number => v !== null && v >= 1 && v <= 5);
  if (ratings.length === 0) return null;
  return Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10;
}

const SatisfactionSurveyAdmin: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{ ids: string[]; single?: boolean } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const { data: surveysData, error: surveysErr } = await supabase
        .from('satisfaction_surveys')
        .select('*')
        .order('created_at', { ascending: false });

      if (surveysErr) throw surveysErr;

      const { data: responsesData, error: responsesErr } = await supabase
        .from('satisfaction_survey_responses')
        .select('*');

      if (responsesErr) throw responsesErr;

      const responsesMap = new Map<string, SurveyResponse>();
      (responsesData || []).forEach((r: any) => responsesMap.set(r.survey_id, r));

      const merged: Survey[] = (surveysData || []).map((s: any) => ({
        ...s,
        response: responsesMap.get(s.id) || undefined,
      }));

      setSurveys(merged);
    } catch (e) {
      console.error('Error loading surveys:', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = surveys.filter((s) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return s.status === 'pending';
    return s.status === 'completed';
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((s) => s.id)));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm?.ids?.length) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('satisfaction_surveys')
        .delete()
        .in('id', deleteConfirm.ids);
      if (error) throw error;
      setSurveys((prev) => prev.filter((s) => !deleteConfirm.ids.includes(s.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        deleteConfirm.ids.forEach((id) => next.delete(id));
        return next;
      });
      setDeleteConfirm(null);
    } catch (e) {
      console.error('Error deleting surveys:', e);
    } finally {
      setDeleting(false);
    }
  };

  // Métricas globales
  const completedWithResponse = surveys.filter((s) => s.status === 'completed' && s.response);
  const indices = completedWithResponse
    .map((s) => computeSatisfactionIndex(s.response!))
    .filter((v): v is number => v !== null);
  const avgIndex = indices.length > 0 ? indices.reduce((a, b) => a + b, 0) / indices.length : 0;

  const wouldReturnPct =
    completedWithResponse.filter((s) => s.response?.q5_would_return === true).length /
    Math.max(1, completedWithResponse.length);
  const wouldRecommendPct =
    completedWithResponse.filter((s) => s.response?.q6_would_recommend === true).length /
    Math.max(1, completedWithResponse.length);

  // Por área (según ponderaciones del usuario)
  const q1Sales = completedWithResponse
    .map((s) => s.response?.q1_experience_rating)
    .filter((v): v is number => v != null);
  const q1Prod = q1Sales; // mismo rating, distinta ponderación
  const q2Prod = completedWithResponse
    .map((s) => s.response?.q2_quality_rating)
    .filter((v): v is number => v != null);
  const q3Prod = completedWithResponse
    .map((s) => s.response?.q3_delivery_rating)
    .filter((v): v is number => v != null);
  const q4Sales = completedWithResponse
    .map((s) => s.response?.q4_communication_rating)
    .filter((v): v is number => v != null);

  const avgQ1 = q1Sales.length ? q1Sales.reduce((a, b) => a + b, 0) / q1Sales.length : 0;
  const avgQ2 = q2Prod.length ? q2Prod.reduce((a, b) => a + b, 0) / q2Prod.length : 0;
  const avgQ3 = q3Prod.length ? q3Prod.reduce((a, b) => a + b, 0) / q3Prod.length : 0;
  const avgQ4 = q4Sales.length ? q4Sales.reduce((a, b) => a + b, 0) / q4Sales.length : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Encuestas de Satisfacción
      </h1>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
            <TrendingUp className="h-4 w-4" />
            Índice de satisfacción
          </div>
          <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
            {avgIndex.toFixed(1)}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Promedio 1-5</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">% Volvería a producir</div>
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">
            {Math.round(wouldReturnPct * 100)}%
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">% Recomendaría</div>
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {Math.round(wouldRecommendPct * 100)}%
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">Completadas / Total</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {completedWithResponse.length} / {surveys.length}
          </div>
        </div>
      </div>

      {/* Métricas por área */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
          <div className="text-sm font-medium text-amber-800 dark:text-amber-200">Ventas (exp. general 70% + comunicación 100%)</div>
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
            {(avgQ1 > 0 || avgQ4 > 0 ? (avgQ1 * 0.7 + avgQ4) / (0.7 + 1) : 0).toFixed(1)}
          </div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
          <div className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Producción (calidad)</div>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
            {avgQ2.toFixed(1)}
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
          <div className="text-sm font-medium text-blue-800 dark:text-blue-200">Producción (tiempos)</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
            {avgQ3.toFixed(1)}
          </div>
        </div>
        <div className="bg-violet-50 dark:bg-violet-900/20 rounded-xl p-4 border border-violet-200 dark:border-violet-800">
          <div className="text-sm font-medium text-violet-800 dark:text-violet-200">Comunicación vendedor</div>
          <div className="text-2xl font-bold text-violet-600 dark:text-violet-400 mt-1">
            {avgQ4.toFixed(1)}
          </div>
        </div>
      </div>

      {/* Filtro y acciones */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
          >
            <option value="all">Todas</option>
            <option value="pending">Pendientes</option>
            <option value="completed">Completadas</option>
          </select>
        </div>
        {selectedIds.size > 0 && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setDeleteConfirm({ ids: Array.from(selectedIds) })}
            icon={<Trash2 className="h-4 w-4" />}
          >
            Eliminar seleccionadas ({selectedIds.size})
          </Button>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && selectedIds.size === filtered.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Entidad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Vendedor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Índice
                  </th>
                  <th className="px-4 py-3 w-20" />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((s) => {
                  const idx = s.response ? computeSatisfactionIndex(s.response) : null;
                  return (
                    <React.Fragment key={s.id}>
                      <tr
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(s.id)}
                            onChange={() => toggleSelect(s.id)}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {s.entity_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {s.entity_type === 'production' ? 'Producción' : 'Muestra'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {s.salesperson}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {format(new Date(s.created_at), 'dd/MM/yyyy', { locale: es })}
                        </td>
                        <td className="px-4 py-3">
                          {s.status === 'completed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                              <CheckCircle className="h-3 w-3" />
                              Completada
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                              <XCircle className="h-3 w-3" />
                              Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {idx !== null ? (
                            <span className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-semibold">
                              <Star className="h-4 w-4 fill-current" />
                              {idx.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm({ ids: [s.id], single: true })}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {expandedId === s.id ? '▲' : '▼'}
                        </td>
                      </tr>
                      {expandedId === s.id && s.response && (
                        <tr>
                          <td colSpan={9} className="px-4 py-4 bg-gray-50 dark:bg-gray-900/50">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                              <div>
                                <strong className="text-gray-700 dark:text-gray-300">Experiencia general:</strong>{' '}
                                {s.response.q1_experience_rating ?? '-'}/5
                              </div>
                              <div>
                                <strong className="text-gray-700 dark:text-gray-300">Calidad producto:</strong>{' '}
                                {s.response.q2_quality_rating ?? '-'}/5
                              </div>
                              <div>
                                <strong className="text-gray-700 dark:text-gray-300">Tiempos entrega:</strong>{' '}
                                {s.response.q3_delivery_rating ?? '-'}/5
                              </div>
                              <div>
                                <strong className="text-gray-700 dark:text-gray-300">Comunicación {s.salesperson}:</strong>{' '}
                                {s.response.q4_communication_rating ?? '-'}/5
                              </div>
                              <div>
                                <strong className="text-gray-700 dark:text-gray-300">Volvería a producir:</strong>{' '}
                                {s.response.q5_would_return === true ? 'Sí' : s.response.q5_would_return === false ? 'No' : '-'}
                              </div>
                              <div>
                                <strong className="text-gray-700 dark:text-gray-300">Recomendaría:</strong>{' '}
                                {s.response.q6_would_recommend === true ? 'Sí' : s.response.q6_would_recommend === false ? 'No' : '-'}
                              </div>
                              {s.response.q6_recommendation_target && (
                                <div className="sm:col-span-2">
                                  <strong className="text-gray-700 dark:text-gray-300">Marca/cliente recomendado:</strong>{' '}
                                  {s.response.q6_recommendation_target}
                                </div>
                              )}
                              {s.response.q7_comment && (
                                <div className="sm:col-span-2">
                                  <strong className="text-gray-700 dark:text-gray-300">Comentario:</strong>{' '}
                                  {s.response.q7_comment}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => !deleting && setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title={deleteConfirm?.single ? 'Eliminar encuesta' : 'Eliminar encuestas'}
        message={
          deleteConfirm?.single
            ? '¿Estás seguro de que deseas eliminar esta encuesta? Esta acción no se puede deshacer.'
            : `¿Estás seguro de que deseas eliminar ${deleteConfirm?.ids?.length || 0} encuestas? Esta acción no se puede deshacer.`
        }
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  );
};

export default SatisfactionSurveyAdmin;
